// ============================================================
// NarrationPanel — Story narration display
// Shows phase labels, move-by-move commentary, key moment callouts
// Handles intro state, guided play, and end state
// ============================================================

import type { NarrationOutput, GameMetadata, PhaseNarration, MomentCallout, MoveCommentary } from "@shared/types"
import type { ReplayStatus, ReplayMode } from "../stores/replay-store"
import styles from "./NarrationPanel.module.css"

interface NarrationPanelProps {
  narration: NarrationOutput
  metadata: GameMetadata
  status: ReplayStatus
  mode: ReplayMode
  currentPhase: PhaseNarration | null
  currentCallout: MomentCallout | null
  currentMoveCommentary: MoveCommentary | null
  currentMoveIndex: number
  totalMoves: number
  onStart: () => void
  onExplore: () => void
  onGuided: () => void
  onReset: () => void
  onCopyLink: () => void
}

export const NarrationPanel = ({
  narration,
  metadata,
  status,
  mode,
  currentPhase,
  currentCallout,
  currentMoveCommentary,
  currentMoveIndex,
  totalMoves,
  onStart,
  onExplore,
  onGuided,
  onReset,
  onCopyLink,
}: NarrationPanelProps) => {
  // --- Intro state ---
  if (status === "intro") {
    return (
      <div className={styles.panel}>
        <div className={styles.introContent}>
          <h1 className={styles.headline}>{narration.storyHeadline}</h1>
          <p className={styles.subheading}>
            {metadata.white} vs. {metadata.black}
            {metadata.opening ? ` \u2014 ${metadata.opening}` : ""}
            {metadata.totalMoves ? `, ${metadata.totalMoves} moves` : ""}
          </p>
          <p className={styles.hook}>{narration.storyHook}</p>
          <button className={styles.startButton} onClick={onStart}>
            Step through the story
          </button>
          <div className={styles.metadataRow}>
            {metadata.whiteElo && (
              <span className={styles.metaItem}>
                {metadata.white} ({metadata.whiteElo})
              </span>
            )}
            {metadata.blackElo && (
              <span className={styles.metaItem}>
                {metadata.black} ({metadata.blackElo})
              </span>
            )}
            {metadata.timeControl && (
              <span className={styles.metaItem}>{metadata.timeControl}</span>
            )}
            <span className={styles.metaItem}>{metadata.result}</span>
          </div>
        </div>
      </div>
    )
  }

  // --- End state ---
  if (status === "ended") {
    return (
      <div className={styles.panel}>
        <div className={styles.endContent}>
          {currentPhase && (
            <>
              <h2 className={styles.phaseLabel}>{currentPhase.phaseLabel}</h2>
              <p className={styles.narrationText}>{currentPhase.narration}</p>
            </>
          )}
          {currentMoveCommentary && (
            <div className={styles.moveCommentary}>
              <p className={styles.commentaryText}>
                {currentMoveCommentary.commentary}
              </p>
            </div>
          )}
          <div className={styles.resultBadge}>
            <span className={styles.resultText}>
              {metadata.result === "1-0"
                ? `${metadata.white} wins`
                : metadata.result === "0-1"
                  ? `${metadata.black} wins`
                  : "Draw"}
            </span>
          </div>
          <div className={styles.ctaGroup}>
            <a href="/" className={styles.ctaPrimary}>
              Create your own replay
            </a>
            <button className={styles.ctaSecondary} onClick={onCopyLink}>
              Copy link
            </button>
            <button className={styles.ctaSecondary} onClick={onExplore}>
              View full game
            </button>
          </div>
        </div>
      </div>
    )
  }

  // --- Guided play state ---
  if (mode === "guided") {
    return (
      <div className={styles.panel}>
        <div className={styles.guidedContent} aria-live="polite">
          {/* Phase label + narration context */}
          {currentPhase ? (
            <div
              className={styles.phaseBlock}
              key={currentPhase.phaseLabel}
            >
              <h2
                className={`${styles.phaseLabel} ${
                  currentPhase.isCritical ? styles.criticalLabel : ""
                }`}
              >
                {currentPhase.phaseLabel}
              </h2>
              <p className={styles.narrationText}>
                {currentPhase.narration}
              </p>
            </div>
          ) : (
            <div className={styles.phaseBlock}>
              <h2 className={styles.phaseLabel}>Starting position</h2>
              <p className={styles.narrationText}>
                Use the arrow keys or buttons to step through the game.
              </p>
            </div>
          )}

          {/* Key moment callout replaces regular commentary */}
          {currentCallout ? (
            <div className={`${styles.callout} ${
              currentCallout.arrows.length > 1 ? styles.calloutCritical : ""
            }`}>
              <p className={styles.calloutText}>{currentCallout.text}</p>
            </div>
          ) : currentMoveCommentary ? (
            <div className={styles.moveCommentary}>
              <p className={styles.commentaryText}>
                {currentMoveCommentary.commentary}
              </p>
            </div>
          ) : null}

          {/* Move counter */}
          <div className={styles.moveCounter}>
            <span>
              Move {Math.max(0, currentMoveIndex + 1)} of {totalMoves}
            </span>
          </div>
        </div>
      </div>
    )
  }

  // --- Free explore mode ---
  return (
    <div className={styles.panel}>
      <div className={styles.exploreContent}>
        <div className={styles.exploreHeader}>
          <h2 className={styles.phaseLabel}>Free Explore</h2>
          <button className={styles.modeToggle} onClick={onGuided}>
            Back to guided story
          </button>
        </div>

        {/* Move commentary in explore mode too */}
        {currentMoveCommentary && (
          <div className={styles.moveCommentary}>
            <p className={styles.commentaryText}>
              {currentMoveCommentary.commentary}
            </p>
          </div>
        )}

        {/* Move list in plain language */}
        <div className={styles.moveList} role="list" aria-label="Move list">
          <button
            className={`${styles.moveListItem} ${
              currentMoveIndex === -1 ? styles.activeMoveItem : ""
            }`}
            onClick={onReset}
          >
            Starting position
          </button>
        </div>

        <div className={styles.moveCounter}>
          <span>
            Move {Math.max(0, currentMoveIndex + 1)} of {totalMoves}
          </span>
        </div>
      </div>
    </div>
  )
}
